import React from "react";
import { useHistory } from "react-router-dom";
import { useTheme } from "../../utils/ThemeContext.jsx";
import logo from "../../assets/images/asset-planet-background.png";
import aiChampIcon from "../../assets/gifs/Ai Robot Vector Art.gif";
import "./FreeTrail.css";

const moduleCards = [
  {
    id: "01",
    range: "Days 1-7",
    title: "The Organizer",
    description:
      "Build your inventory. Sync your accounts and documents to see your true net worth.",
    accentClass: "freetrail-module-card--blue",
  },
  {
    id: "02",
    range: "Days 8-14",
    title: "The Planner",
    description:
      "Model your future. Run simulations to see your probability of retirement success.",
    accentClass: "freetrail-module-card--green",
  },
  {
    id: "03",
    range: "Days 15-21",
    title: "The Protector",
    description:
      "Secure your legacy. Audit your beneficiaries and insurance adequacy.",
    accentClass: "freetrail-module-card--purple",
  },
];

const milestones = [
  { day: "Day 1", label: "Activation", accentClass: "is-active" },
  { day: "Day 7", label: "Planner", accentClass: "is-green" },
  { day: "Day 14", label: "Protector", accentClass: "is-purple" },
  { day: "Day 21", label: "Your Plan", accentClass: "is-neutral" },
];

const professionalModuleCards = [
  {
    range: "Days 1-7",
    title: "The Organizer",
    description:
      "Build each client's full financial inventory. True net worth, at a glance.",
    accentClass: "freetrail-module-card--blue",
  },
  {
    range: "Days 8-14",
    title: "The Planner",
    description:
      "Run retirement simulations and model probability of success for every client.",
    accentClass: "freetrail-module-card--green",
  },
  {
    range: "Days 15-21",
    title: "The Protector",
    description:
      "Audit beneficiaries and insurance adequacy. Secure every client's legacy.",
    accentClass: "freetrail-module-card--purple",
  },
];

const professionalRoster = [
  { initials: "JM", name: "James & Maria Chen", status: "Active" },
  { initials: "RP", name: "Robert Patel", status: "Active" },
  { initials: "SL", name: "Sandra Liu", status: "Active" },
  { initials: "TW", name: "Thomas & Wei Williams", status: "Inactive" },
];

const professionalPlatformGroups = [
  {
    title: "Organizer",
    accentClass: "is-blue",
    items: [
      "Assets",
      "Liabilities",
      "Insurance",
      "Income",
      "Budget",
      "Important Documents",
    ],
  },
  {
    title: "Planner",
    accentClass: "is-green",
    items: ["Debt Payoff", "Social Security", "Goals"],
  },
  {
    title: "Protector",
    accentClass: "is-purple",
    items: ["Death", "Disability", "Divorce", "Disaster"],
  },
];

const professionalTools = [
  {
    accentClass: "is-blue",
    title: "Secure client messaging",
    description:
      "Chat securely with each client directly inside the platform. All conversations stay within Asset Planet.",
  },
  {
    accentClass: "is-green",
    title: "Report generation",
    description:
      "Create comprehensive financial reports for clients - net worth, projections, insurance audits and more.",
  },
  {
    accentClass: "is-purple",
    title: "Expiring document links",
    description:
      "Share documents via secure links that automatically expire - you set the timeframe, you control access.",
  },
  {
    accentClass: "is-teal",
    title: "Institution connectivity",
    description:
      "Connect financial institutions directly to consolidate client accounts and sync balances automatically.",
  },
];

const professionalMobileApps = [
  { title: "Legacy Planning", badge: "Death module" },
  { title: "Debt Payoff", badge: "Free to clients" },
  { title: "Inventory & Collectibles", badge: "Free to clients" },
];

const ProfessionalInfoCard = ({ accentClass, title, subtitle, children }) => (
  <article className={`professional-info-card ${accentClass || ""}`}>
    <div className="professional-info-card__icon" aria-hidden="true" />
    <div className="professional-info-card__body">
      <h3>{title}</h3>
      {subtitle && <p className="professional-info-card__subtitle">{subtitle}</p>}
      {children}
    </div>
  </article>
);

const StandardFreeTrailContent = () => {
  const history = useHistory();
  const { theme } = useTheme();

  return (
    <div
      className={`freetrail-screen ${
        theme === "dark" ? "freetrail-screen--dark" : ""
      }`}
    >
      <div className="freetrail-shell">
        <div className="freetrail-brand">
          <img
            className="freetrail-brand-logo"
            src={logo}
            alt="Asset Planet"
          />
        </div>

        <div className="freetrail-badge">
          Your 21-day concierge period has begun
        </div>

        <div className="freetrail-hero">
          <h1 className="freetrail-title">
            Welcome to Your Command Center.
            <br />
            Your 21-Day Journey to Financial Clarity Starts Now.
          </h1>
          <p className="freetrail-subtitle">
            Three modules. One integrated plan. A concierge experience built
            for you.
          </p>
        </div>

        <div className="freetrail-modules-grid">
          {moduleCards.map((card) => (
            <article
              key={card.id}
              className={`freetrail-module-card ${card.accentClass}`}
            >
              <div className="freetrail-module-range">{card.range}</div>
              <h2>{card.title}</h2>
              <p>{card.description}</p>
            </article>
          ))}
        </div>

        <section className="freetrail-ai-card">
          <div className="freetrail-ai-icon" aria-hidden="true">
            <img src={aiChampIcon} alt="AI companion" style={{ width: "48px", height: "48px", objectFit: "cover", borderRadius: "50%" }} />
          </div>
          <div className="freetrail-ai-copy">
            <div className="freetrail-ai-eyebrow">Meet Your AI Companion</div>
            <p>
              Once you enter, look for the <strong style={{color:"#2a3da3"}}>AI Companion</strong> bar at the top of your
              dashboard. It is trained on our documentation to guide you
              through every module.
            </p>
          </div>
          <div className="freetrail-ai-question">
            "How do I start my Organizer?"
          </div>
        </section>

        <section className="freetrail-timeline-card">
          <div className="freetrail-timeline-header">
            <span>Your 21-Day Flight Path</span>
            <span>Day 1 of 21</span>
          </div>

          <div className="freetrail-timeline-track">
            <div className="freetrail-timeline-progress" />
            {milestones.map((step) => (
              <div
                key={step.day}
                className={`freetrail-timeline-step ${step.accentClass}`}
              >
                <div className="freetrail-timeline-dot" />
                <div className="freetrail-timeline-label">
                  <strong>{step.day}</strong>
                  <span>{step.label}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="freetrail-cta-wrap">
          <button
            className="freetrail-cta-button"
            type="button"
            onClick={() => history.push("/questioner")}
          >
            Begin My Setup - 12 Quick Questions
          </button>
          <p className="freetrail-cta-note">
            Takes about 3 minutes - your answers personalize your dashboard
            automatically.
          </p>
          <p className="freetrail-cta-disclaimer">
            Projections are for illustrative purposes and are not guarantees of
            future results.
          </p>
        </div>
      </div>
    </div>
  );
};

const ProfessionalFreeTrailContent = () => {
  const history = useHistory();
  const { theme } = useTheme();

  return (
    <div
      className={`freetrail-screen freetrail-screen--professional ${
        theme === "dark" ? "freetrail-screen--dark" : ""
      }`}
    >
      <div className="freetrail-shell professional-freetrail-shell">
        <div className="freetrail-brand professional-freetrail-brand">
          <img
            className="freetrail-brand-logo professional-freetrail-brand-logo"
            src={logo}
            alt="Asset Planet"
          />
        </div>

        <div className="professional-edition-pill">Professional Edition</div>

        <div className="freetrail-hero professional-freetrail-hero">
          <h1 className="freetrail-title professional-freetrail-title">
            Welcome to Your Practice Command Center.
            <br />
            Manage Every Client. Monitor Everything.
          </h1>
          <p className="freetrail-subtitle professional-freetrail-subtitle">
            Your complete financial advisory platform - assets, liabilities,
            income, insurance, budgets, goals and more.
          </p>
        </div>

        <div className="professional-concierge-banner">
          Your 21-Day Concierge Period Has Begun. Full access to every module
          and client tool - no limits.
        </div>

        <section className="professional-section">
          <div className="professional-section__eyebrow">
            Your Three Core Modules
          </div>
          <div className="freetrail-modules-grid professional-modules-grid">
            {professionalModuleCards.map((card) => (
              <article
                key={card.title}
                className={`freetrail-module-card professional-module-card ${card.accentClass}`}
              >
                <div className="freetrail-module-range">{card.range}</div>
                <h2>{card.title}</h2>
                <p>{card.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="professional-section">
          <div className="professional-section__eyebrow">
            Client Management & Platform Modules
          </div>
          <div className="professional-split-grid">
            <ProfessionalInfoCard
              accentClass="is-blue"
              title="Your client roster"
              subtitle="Unlimited clients - 10 active at a time"
            >
              <div className="professional-roster-list">
                {professionalRoster.map((client) => (
                  <div key={client.name} className="professional-roster-item">
                    <div className="professional-roster-item__identity">
                      <span className="professional-roster-item__avatar">
                        {client.initials}
                      </span>
                      <span>{client.name}</span>
                    </div>
                    <span
                      className={`professional-status-pill ${
                        client.status === "Inactive" ? "is-inactive" : ""
                      }`}
                    >
                      {client.status}
                    </span>
                  </div>
                ))}
              </div>

              <div className="professional-stats-grid">
                <div className="professional-stat-card">
                  <strong>Unlimited</strong>
                  <span>Total clients</span>
                </div>
                <div className="professional-stat-card">
                  <strong>10</strong>
                  <span>Active & synced</span>
                </div>
                <div className="professional-stat-card">
                  <strong>Full</strong>
                  <span>Two-way sync</span>
                </div>
              </div>
            </ProfessionalInfoCard>

            <ProfessionalInfoCard
              accentClass="is-green"
              title="All platform modules"
              subtitle="Grouped by pillar"
            >
              <div className="professional-pillars">
                {professionalPlatformGroups.map((group) => (
                  <div
                    key={group.title}
                    className={`professional-pillar ${group.accentClass}`}
                  >
                    <div className="professional-pillar__title">
                      {group.title}
                    </div>
                    <div className="professional-pillar__chips">
                      {group.items.map((item) => (
                        <span key={item} className="professional-chip">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </ProfessionalInfoCard>
          </div>
        </section>

        <section className="professional-section">
          <div className="professional-section__eyebrow">
            Exclusive Professional Tools
          </div>
          <div className="professional-tools-grid">
            {professionalTools.map((tool) => (
              <ProfessionalInfoCard
                key={tool.title}
                accentClass={tool.accentClass}
                title={tool.title}
              >
                <p>{tool.description}</p>
              </ProfessionalInfoCard>
            ))}
          </div>
        </section>

        <section className="professional-highlight-card">
          <div className="professional-highlight-card__icon" aria-hidden="true" />
          <div>
            <h3>Your platform, your finances too</h3>
            <p>
              As a Professional subscriber, you also get full personal access
              to the entire Asset Planet platform - every module, for your own
              financial life. Manage your clients and your own wealth in one
              place.
            </p>
          </div>
        </section>

        <section className="professional-mobile-card">
          <div className="professional-mobile-card__header">
            <div>
              <h3>Free mobile apps - included for all clients</h3>
              <p>
                Seamlessly synchronize with the desktop platform in real time.
              </p>
            </div>
            <span className="professional-status-pill is-live">Live sync</span>
          </div>

          <div className="professional-mobile-grid">
            {professionalMobileApps.map((app) => (
              <div key={app.title} className="professional-mobile-app">
                <strong>{app.title}</strong>
                <span>{app.badge}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="freetrail-ai-card professional-ai-card">
          <div className="freetrail-ai-icon" aria-hidden="true">
            <img
              src={aiChampIcon}
              alt="AI companion"
              style={{
                width: "48px",
                height: "48px",
                objectFit: "cover",
                borderRadius: "50%",
              }}
            />
          </div>
          <div className="freetrail-ai-copy">
            <div className="freetrail-ai-eyebrow">Meet Your AI Companion</div>
            <p>
              Your dashboard includes an <strong>AI Companion</strong> bar
              trained on our full documentation - ready to guide you through
              every module and client workflow.
            </p>
          </div>
          <div className="freetrail-ai-question">
            "How do I add my first client?"
          </div>
        </section>

        <section className="freetrail-timeline-card professional-timeline-card">
          <div className="freetrail-timeline-header">
            <span>Your 21-Day Flight Path</span>
            <span>Day 1 of 21</span>
          </div>

          <div className="freetrail-timeline-track">
            <div className="freetrail-timeline-progress" />
            {milestones.map((step) => (
              <div
                key={step.day}
                className={`freetrail-timeline-step ${step.accentClass}`}
              >
                <div className="freetrail-timeline-dot" />
                <div className="freetrail-timeline-label">
                  <strong>{step.day}</strong>
                  <span>{step.label}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="freetrail-cta-wrap">
          <button
            className="freetrail-cta-button"
            type="button"
            onClick={() => history.push("/questioner")}
          >
            Begin My Practice Setup - 12 Quick Questions
          </button>
          <p className="freetrail-cta-note">
            Takes about 3 minutes - your answers personalise your advisor
            dashboard and default client templates automatically.
          </p>
          <p className="freetrail-cta-disclaimer">
            Projections are for illustrative purposes and are not guarantees of
            future results.
          </p>
        </div>
      </div>
    </div>
  );
};

export const ProfessionalFreeTrailPage = ProfessionalFreeTrailContent;

export const FreeTrailPage = () => {
  const role =
    localStorage.getItem("role") !== "undefined" &&
    JSON.parse(localStorage.getItem("role") || "null");

  const normalizedRole = typeof role === "string" ? role.trim().toLowerCase() : "";
  const professionalRoles = [
    "financial advisor",
    "active financial advisor",
    "financial adviser",
    "active financial adviser",
  ];

  return professionalRoles.includes(normalizedRole) ? (
    <ProfessionalFreeTrailContent />
  ) : (
    <StandardFreeTrailContent />
  );
};
